import React from 'react'

import CsvDownloadLink from 'components/CsvDownloadLink'
import { FileUpload } from 'components/FileUpload/FileUpload'
import { domains } from 'lib/config'
import Loading from 'components/Loading/Loading'
import {
  BROWSE,
  DOWNLOAD_RESULTS_LINK_LABEL,
  SITE_PREFERENCES_TOOL_TITLE,
  TOOL_DESCRIPTION,
  UPLOAD,
} from './constants'
import { csvTemplateHeaders } from './data'
import {
  ExampleImage,
  Instructions,
  ResultsSection,
  StyledAutoTable,
  TemplateLinkWrapper,
  UploadButton,
  UploadSection,
} from './styles'

export interface FormattedUploadResult {
  siteId?: number | string
  siteName?: string
  rowNumber?: number
  error?: string
}

export interface SitePreferencesProps {
  error?: string | null
  file?: File | null
  fileName: string
  handleUpload: (props: { file?: File | null }) => void
  isLoading: boolean
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  formattedUploadResults?: FormattedUploadResult[]
}

interface Column {
  title: string
  fieldValue: (row: FormattedUploadResult) => number | string | null
  sortValue?: string
}

const headers = [
  { label: 'Site Id', key: 'siteId' },
  { label: 'siteName', key: 'siteName' },
  { label: 'error', key: 'error' },
]

const columns: Column[] = [
  {
    title: 'Site ID',
    fieldValue: row => row.siteId ?? null,
    sortValue: 'siteId',
  },
  {
    title: 'Site Name',
    fieldValue: row => row.siteName ?? null,
    sortValue: 'siteName',
  },
  {
    title: 'Error',
    fieldValue: row => row.error ?? null,
    sortValue: 'error',
  },
]

const SitePreferences: React.FC<SitePreferencesProps> = props => {
  const { file, fileName, isLoading, onFileChange, formattedUploadResults } =
    props

  return (
    <>
      <Loading isLoading={isLoading} message="Processing file...." />
      <h2>{SITE_PREFERENCES_TOOL_TITLE}</h2>
      <hr />
      <h3>Tool Description</h3>
      <p>{TOOL_DESCRIPTION}</p>

      <TemplateLinkWrapper>
        <CsvDownloadLink
          autoClick={false}
          data={[{}]}
          filename={'site_preferences_template.csv'}
          headers={csvTemplateHeaders}
        >
          Download Site Preferences Template
        </CsvDownloadLink>
      </TemplateLinkWrapper>

      <Instructions>
        Upload your <code>.xlsx</code> or <code>.csv</code> in the format below.
      </Instructions>
      <FileUpload
        disabled={isLoading}
        fileName={fileName}
        key={fileName}
        onFileChange={onFileChange}
      >
        {BROWSE}
      </FileUpload>
      <UploadSection>
        <div>
          <UploadButton
            disabled={!file || isLoading}
            onClick={() => props.handleUpload({ ...props })}
          >
            {UPLOAD}
          </UploadButton>
        </div>
      </UploadSection>
      <ResultsSection>
        {formattedUploadResults && (
          <>
            <TemplateLinkWrapper>
              <CsvDownloadLink
                autoClick={false}
                data={formattedUploadResults}
                filename={
                  new Date().toISOString() + '_site_preferences_results.csv'
                }
                headers={headers}
              >
                {DOWNLOAD_RESULTS_LINK_LABEL}
              </CsvDownloadLink>
            </TemplateLinkWrapper>
            <StyledAutoTable
              borderless
              columns={columns}
              headThemeLight
              rows={formattedUploadResults}
            />
          </>
        )}
      </ResultsSection>
      <h4>Fields Description</h4>
      <h5>
        For a complete list of fields that can be updated,{' '}
        <a
          href="https://secondstep.atlassian.net/wiki/spaces/PE/pages/3293184002/Available+Site+Preferences"
          rel="noopener noreferrer"
          target="_blank"
        >
          see the documentation.{' '}
        </a>
      </h5>
      <Instructions>
        All uploaded spreadsheets should be formatted like this:
      </Instructions>
      <ExampleImage
        src={`${domains.CDN_DOMAIN}/assets/img/sites_preferences_preview.png`}
      />
    </>
  )
}

export default SitePreferences
